import type { Point } from '../../../../core/types/point.js';
import { isBandScale } from '../../../../core/utils/detect-scale.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { scaleBandInvert } from '../../../../core/utils/scale.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import type {
  AnyXYChartScale,
  RECT_VARIANT,
  RectSeriesWithDatapoints,
  DOT_VARIANT,
  DotSeriesWithDatapoints,
  WeakMapDatapoints,
  RectDatapointInternal,
} from '../../../types/xy-chart-internals.js';
import type {
  DotDatapoint,
  XYAccessorDataTypes,
} from '../../../types/xy-chart.js';
import { getCurrentScale } from '../../../utils/get-scale.js';

function getDomainFromScale(
  scales: { xScale: AnyXYChartScale; yScale: AnyXYChartScale },
  position: Point,
): XYAccessorDataTypes[] {
  let xHoveredDomain;
  let yHoveredDomain;

  if (isBandScale(scales.xScale)) {
    xHoveredDomain = scaleBandInvert(scales.xScale)(position.x);
  } else {
    xHoveredDomain = scales.xScale.invert(position.x);
  }

  if (isBandScale(scales.yScale)) {
    yHoveredDomain = scaleBandInvert(scales.yScale)(position.y);
  } else {
    yHoveredDomain = scales.yScale.invert(position.y);
  }

  return [xHoveredDomain, yHoveredDomain];
}

/**
 * Retrieves the datapoint that is currently being hovered over based on the given position.
 *
 * @param position - The current mouse position.
 * @param series - The series containing data points.
 * @param axisScales - A map of axis IDs to their respective scales.
 * @returns - The datapoint being hovered over, or undefined if no datapoint is hovered.
 */

type Variants = {
  [RECT_VARIANT]: {
    datapoint: RectDatapointInternal;
    series: RectSeriesWithDatapoints;
  };
  [DOT_VARIANT]: {
    datapoint: DotDatapoint;
    series: DotSeriesWithDatapoints;
  };
};

export function getHoveredDatapoint<K extends keyof Variants>(
  position: Point,
  series: Variants[K]['series'],
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: WeakMapDatapoints,
  isDisplayCallbackCondition: (
    xScale: AnyXYChartScale,
    yScale: AnyXYChartScale,
    datapoint: Variants[K]['datapoint'],
    xHoveredDomain: XYAccessorDataTypes,
    yHoveredDomain: XYAccessorDataTypes,
  ) => boolean,
): Variants[K]['datapoint'] | undefined {
  const { data, xAxisId, yAxisId } = series;

  const xScale = getCurrentScale(axisIdToScales, xAxisId);
  const yScale = getCurrentScale(axisIdToScales, yAxisId);

  if (position.x < 0 || position.y < 0) {
    return undefined;
  }

  const [xHoveredDomain, yHoveredDomain] = getDomainFromScale(
    { xScale, yScale },
    position,
  );

  for (const datapoint of data) {
    if (safeGet(datapointMetadataMap, datapoint).isGap) {
      continue;
    }

    if (
      isDisplayCallbackCondition(
        xScale,
        yScale,
        datapoint,
        xHoveredDomain,
        yHoveredDomain,
      )
    ) {
      return datapoint;
    }
  }

  return undefined;
}
